export type Platform = "wortal" | "link" | "viber" | "facebook" | "gd" | "game_distribution" | "crazygames" | "crazy_games" |
  "gamepix" | "telegram" | "gamemonetize" | "debug"

export interface BaseOption {
  baseUrl?: string
  game_id: number
}

export interface sendOtpOption extends BaseOption {
  email: string,
  platform: Platform,
  delivery_mechanism?: "email"
}

export interface verifyOtpOption extends BaseOption {
  email: string,
  platform: Platform,
  otp: string | number,
  token: string
}

export interface AuthenticatedOption extends BaseOption {
  auth_token: string
}

export interface saveDataOption<T=any> extends AuthenticatedOption {
  game_data: T
}

export interface savePartialDataOption<T=any> extends AuthenticatedOption {
  game_data: Partial<T>
}

export interface getDataOption extends AuthenticatedOption {
}

export const DEFAULT_BASE_URL = "https://waves.html5gameportal.com"

export async function sendOtp({ baseUrl, platform, delivery_mechanism, ...rest }: sendOtpOption): Promise<string> {
  baseUrl = baseUrl ?? DEFAULT_BASE_URL

  const response = await fetch(`${baseUrl}/api/v1/${platform}/auth`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      ...rest,
      delivery_mechanism: delivery_mechanism ?? "email"
    })
  })

  if (response.status !== 200) {
    if (response.headers.get("Content-Type") === "application/json") {
      const json = await response.json()
      throw new Error(json.error)
    }
    throw new Error("Failed to send OTP")
  }

  const json = await response.json()
  return json.auth_token
}

export async function verifyOtp({baseUrl, platform, ...rest}: verifyOtpOption): Promise<void> {
  baseUrl = baseUrl ?? DEFAULT_BASE_URL

  const response = await fetch(`${baseUrl}/api/v1/${platform}/verify-otp`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(rest)
  })

  if (response.status !== 200) {
    if (response.headers.get("Content-Type") === "application/json") {
      const json = await response.json()
      throw new Error(json.error)
    }
    throw new Error("Failed to verify OTP")
  }
}

export async function saveData<T = any>({baseUrl, ...rest}: saveDataOption<T>): Promise<{ message: string }> {
  baseUrl = baseUrl ?? DEFAULT_BASE_URL

  const response = await fetch(`${baseUrl}/api/v1/save`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(rest)
  })

  if (response.status !== 200) {
    if (response.headers.get("Content-Type") === "application/json") {
      const json = await response.json()
      throw new Error(json.error)
    }
    throw new Error("Failed to save data")
  }

  return await response.json()
}

export async function savePartialData<T = any>({baseUrl, ...rest}: savePartialDataOption<T>): Promise<{ message: string }> {
  baseUrl = baseUrl ?? DEFAULT_BASE_URL

  const response = await fetch(`${baseUrl}/api/v1/save`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(rest)
  })

  if (response.status !== 200) {
    if (response.headers.get("Content-Type") === "application/json") {
      const json = await response.json()
      throw new Error(json.error)
    }
    throw new Error("Failed to save data")
  }

  return await response.json()
}

export async function getData<T = any>({baseUrl, ...rest}: getDataOption): Promise<T> {
  baseUrl = baseUrl ?? DEFAULT_BASE_URL

  const response = await fetch(`${baseUrl}/api/v1/get`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(rest)
  })

  if (response.status !== 200) {
    if (response.headers.get("Content-Type") === "application/json") {
      const json = await response.json()
      throw new Error(json.error)
    }
    throw new Error("Failed to get data")
  }

  const data = await response.json()
  return data.game_data as T
}

